import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PresetCard from './PresetCard';
import { useTranslation } from 'react-i18next';

const INITIAL_VISIBLE_PRESETS = 4;

function isWidePreset(preset) {
  return preset?.cardLayout === 'wide' && Boolean(preset?.wideImage);
}

function buildPresetRows(presets) {
  const rows = [];
  let pendingNormal = [];

  for (let index = 0; index < presets.length; index += 1) {
    const preset = presets[index];
    if (isWidePreset(preset)) {
      if (pendingNormal.length > 0) {
        rows.push({ type: 'normal', items: pendingNormal });
        pendingNormal = [];
      }
      rows.push({ type: 'wide', item: { preset, index } });
      continue;
    }

    pendingNormal.push({ preset, index });
    if (pendingNormal.length === 2) {
      rows.push({ type: 'normal', items: pendingNormal });
      pendingNormal = [];
    }
  }

  if (pendingNormal.length > 0) rows.push({ type: 'normal', items: pendingNormal });
  return rows;
}

/**
 * PresetsGrid — 2-колоночная сетка пресетов в группе.
 *
 * Figma source: `318:4668` cards frame, layoutMode=GRID, 2 cols,
 * Uses the same card gap token as the home screen.
 *
 * Empty state (handoff §6): «Пресеты отсутствуют» в центре при `presets=[]`.
 *
 * Это композитный wrapper — primitives не импортирует напрямую, но
 * композирует PresetCard, который использует Cover primitive.
 *
 * Props:
 *   - presets      — массив пресетов (`[{ id, image, isNew? }, …]`)
 *   - onTapPreset  — (preset) => void — прокидывается в PresetCard.onTap
 */
export default function PresetsGrid({ presets, onTapPreset }) {
  const { t } = useTranslation();
  const itemRefs = useRef([]);
  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(INITIAL_VISIBLE_PRESETS, presets?.length || 0)
  );
  const rows = useMemo(() => buildPresetRows(presets || []), [presets]);

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, presets?.length || 0);
    setVisibleCount(Math.min(INITIAL_VISIBLE_PRESETS, presets?.length || 0));
  }, [presets]);

  const revealVisibleRows = useCallback(() => {
    if (!Array.isArray(presets) || presets.length <= INITIAL_VISIBLE_PRESETS) return;
    if (typeof window === 'undefined') return;

    const viewportBottom =
      window.innerHeight || document.documentElement?.clientHeight || 0;
    const preloadPx = Math.max(160, Math.round(viewportBottom * 0.25));

    setVisibleCount((current) => {
      let next = current;

      for (let index = current; index < presets.length; index += 1) {
        const node = itemRefs.current[index];
        if (!node) continue;

        const rect = node.getBoundingClientRect();
        if (rect.top === 0 && rect.bottom === 0) continue;

        if (rect.top <= viewportBottom + preloadPx) {
          next = Math.max(next, Math.min(presets.length, Math.ceil((index + 1) / 2) * 2));
        }
      }

      return next === current ? current : next;
    });
  }, [presets]);

  useEffect(() => {
    if (!Array.isArray(presets) || presets.length <= INITIAL_VISIBLE_PRESETS) return undefined;
    if (typeof window === 'undefined') return undefined;

    let frame = 0;
    const onViewportChange = () => {
      const scrollY =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement?.scrollTop ||
        document.body?.scrollTop ||
        0;
      if (scrollY <= 1) return;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(revealVisibleRows);
    };

    window.addEventListener('scroll', onViewportChange, { passive: true });

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onViewportChange);
    };
  }, [presets, revealVisibleRows]);

  if (!presets || presets.length === 0) {
    return (
      <div className="text-text-mute text-center py-16">
        {t('gallery.emptyCatalog')}
      </div>
    );
  }

  return (
    <div
      data-testid="presets-grid"
      className="flex flex-col gap-gap-cards"
    >
      {rows.map((row, rowIndex) => {
        if (row.type === 'wide') {
          const { preset, index } = row.item;
          return (
            <div
              key={preset.id}
              ref={(node) => {
                itemRefs.current[index] = node;
              }}
              className="w-full"
              style={{ aspectRatio: '16 / 9' }}
              data-testid="preset-row-wide"
            >
              {index < visibleCount ? (
                <PresetCard
                  preset={preset}
                  coverUrl={preset.wideImage}
                  aspectRatio="16 / 9"
                  eager={index < INITIAL_VISIBLE_PRESETS}
                  onTap={onTapPreset}
                />
              ) : (
                <div
                  aria-hidden="true"
                  className="h-full w-full rounded-home-16 bg-white/[0.06]"
                  data-testid="preset-card-placeholder"
                />
              )}
            </div>
          );
        }

        return (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`row-${rowIndex}`}
            className="grid grid-cols-2 gap-gap-cards"
            data-testid="preset-row-normal"
          >
            {row.items.map(({ preset, index }) => (
              <div
                key={preset.id}
                ref={(node) => {
                  itemRefs.current[index] = node;
                }}
                className="w-full"
                style={{ aspectRatio: '3 / 4' }}
              >
                {index < visibleCount ? (
                  <PresetCard
                    preset={preset}
                    eager={index < INITIAL_VISIBLE_PRESETS}
                    onTap={onTapPreset}
                  />
                ) : (
                  <div
                    aria-hidden="true"
                    className="h-full w-full rounded-home-16 bg-white/[0.06]"
                    data-testid="preset-card-placeholder"
                  />
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
